import { useState } from 'react';
import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import { Alert, AlertTitle, Skeleton, Typography, Link } from '@mui/material';
import { useParams } from 'react-router';
import { fontFamily } from '@/@mui/tokens/font-family';
import { HowRefundWorksDialog } from '@/components/partial/how-refund-works-dialog';
import { useCourseDetails, type CourseDetails } from '@/data/catalog/course';
import { useCalculateCourseRefundableAmount } from '@/data/subsidy/policies/calculate-course-refundable-amount';
import { PriceTypes } from '@/models/catalog/course';
import type { ProductOfferSearch } from '@/models/catalog/offer';
import { useOffer } from '@/packages/courses/pages/details/context/offer';
import { formatMoney } from '@/utils/format-money';
import { refundPolicyTexts } from '@/utils/refund-policy-texts';

const buildSubsidyRequestParams = (
  course?: CourseDetails['course'],
  offer?: ProductOfferSearch['cheapest_offer']
) => {
  return {
    course: {
      id: course?.id ?? 0,
      offer_id: offer?.id ?? 0,
      checkout_type: 'internal',
      course_type: course?.course_type.type ?? '',
      duration_type: course?.duration.type ?? '',
      duration: course?.duration.quantity ?? 0,
      price: offer?.pricing_description.sale_price ?? 0,
      type: offer?.pricing_description.type ?? '',
    },
  };
};

export const Refund = () => {
  const { id } = useParams();
  const { details } = useCourseDetails(Number(id));
  const { cheapestOffer, pricing, isLoading } = useOffer();
  const [openHowRefundWorksDialog, setOpenHowRefundWorksDialog] =
    useState(false);

  const subsidyRequestParams = buildSubsidyRequestParams(
    details?.course,
    cheapestOffer
  );
  const hasPrice = !isLoading && subsidyRequestParams?.course?.price > 0;

  const { data: subsidy, isLoading: subsidyLoading } =
    useCalculateCourseRefundableAmount(subsidyRequestParams, hasPrice);

  const loading = isLoading || subsidyLoading;

  const policyLimits = subsidy?.refundable_value?.policy_limits;
  const amountAvailable = policyLimits?.amount_available;

  const noRefund =
    !cheapestOffer ||
    !subsidy ||
    !subsidy?.refundable_value ||
    (subsidy.refundable_value.user_available_balance &&
      Number(subsidy.refundable_value.user_available_balance) === 0) ||
    (amountAvailable !== undefined && amountAvailable !== null && Number(amountAvailable) === 0);

  if (noRefund) return null;

  const isTuition = pricing?.type === PriceTypes.Tuition;

  const refundableAmount = Number(subsidy?.refundable_value?.refundable_amount ?? 0);

  const policyTexts = policyLimits
    ? refundPolicyTexts({ policyLimits, refundableAmount })
    : null;

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={120} />;
  }

  return (
    <Alert
      icon={<CurrencyExchangeOutlined fontSize="medium" />}
      sx={(theme) => ({
        background: theme.palette.success.light,
      })}
    >
      <AlertTitle color="success.dark">
        {policyTexts ? policyTexts.title : isTuition ? (
          <>
            Com Reembolso
            <br />A partir de{' '}
            {formatMoney(
              subsidy?.refundable_value?.course_price
                .final_price_with_reimbursement ?? 0
            )}
            /mês
          </>
        ) : (
          <>
            Com Reembolso
            <br />A partir de{' '}
            {formatMoney(
              subsidy?.refundable_value?.course_price
                .final_price_with_reimbursement ?? 0
            )}
          </>
        )}
      </AlertTitle>
      <Typography
        sx={(theme) => ({ color: theme.palette.success.dark })}
        variant="body2"
        fontFamily={fontFamily.fontFamilyCarbona}
      >
        {policyTexts ? policyTexts.subtitle + ' ' : 'Você pode pedir reembolso conforme as regras da sua empresa. '}
        <Link
          sx={(theme) => ({
            color: theme.palette.success.dark,
            textDecorationColor: theme.palette.success.dark,
            fontFamily: fontFamily.fontFamilyCarbona,
            fontSize: '13px',
          })}
          onClick={() => {
            setOpenHowRefundWorksDialog(true);
          }}
        >
          {policyTexts ? policyTexts.linkLabel : 'Como funciona?'}
        </Link>
      </Typography>
      {openHowRefundWorksDialog && (
        <HowRefundWorksDialog
          onClose={() => setOpenHowRefundWorksDialog(false)}
        />
      )}
    </Alert>
  );
};
